/* React */
import React, { useState, useEffect, useCallback } from 'react';

/* Components */
import GameLayout from '../../components/Layout';
import Keyboard from './Components/Keyboard';
import { Rules } from './Components/RulesModal';
import { Results } from './Components/ResultModal';
import { GameBoard } from './Components/GameBoard';

/* Interfaces */
import { getWord, IBoard, verifyWord } from './interfaces/WordleInterface';

/* Styles */
import {
  Container,
  Content,
  GameContainer,
  GameContent,
  LoadingPage,
  ModalError,
  ModalWarning,
} from './style';

/* Crypto */
import CryptoJS from 'crypto-js';

/* Assets */
import Logo from './assets/logo.png';
import OwlError from './assets/owl_error.png';

/* Hooks */
import { useWordle } from '../WordleContext';
import { BorderOptions } from './Components/BorderColorModal';
import { GameHeader } from '../../../components/GameHeader';
import { IoTriangleSharp } from 'react-icons/io5';
import { Sidebar } from './Components/Sidebar';

const Wordle: React.FC = () => {
  /* Dados do jogo */
  const [boardData, setBoardData] = useState<IBoard>(
    localStorage.getItem('@Portal:Wordle')
      ? JSON.parse(localStorage.getItem('@Portal:Wordle') || '')
      : null
  );
  const [error, setError] = useState(false);
  const [charArray, setCharArray] = useState<string[]>([]);
  const [time, setTime] = useState(0);
  const [score, setScore] = useState(0);
  const [isOn, setIsOn] = useState(
    localStorage.getItem('@PortalWordle:time') ? false : true
  );
  const [loading, setLoading] = useState(false);
  //const [repeatedCharacters, setRepeatedCharacters] = useState<string[]>([]);
  const [winCondition, setWinCondition] = useState('');

  /* Modais */
  const [openModalWarning, setOpenModalWarning] = useState(false);
  const [openModalInvalidWord, setOpenModalInvalidWord] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [openModalWaiting, setOpenModalWaiting] = useState(false);

  /* Context */
  const {
    openModalExplanatory,
    setOpenModalExplanatory,
    openModalResult,
    setOpenModalResult,
    setAnimation0,
    setAnimation1,
    setAnimation2,
    setAnimation3,
    setAnimation4,
    setAnimation5,
    openModalBorderColor,
    setOpenModalBorderColor,
  } = useWordle();

  /* Palavra chave, com e sem acento e definição */
  const initialWord = localStorage.getItem('@PortalWordle:solution');
  const accentedInitial = localStorage.getItem(
    '@PortalWordle:accentedSolution'
  );
  const initialDefinition = localStorage.getItem('@PortalWordle:definition');

  const [keyWord, setKeyWord] = useState(
    CryptoJS.AES.decrypt(
      initialWord || '',
      '5c81556179d6bfb07baf4badff8d9b3eb51b913f'
    ).toString(CryptoJS.enc.Utf8)
  );
  const [finalAnswer, setFinalAnswer] = useState(
    CryptoJS.AES.decrypt(
      accentedInitial || '',
      '5c81556179d6bfb07baf4badff8d9b3eb51b913f'
    ).toString(CryptoJS.enc.Utf8)
  );
  const [definition, setDefinition] = useState(
    CryptoJS.AES.decrypt(
      initialDefinition || '',
      '5c81556179d6bfb07baf4badff8d9b3eb51b913f'
    ).toString(CryptoJS.enc.Utf8)
  );

  const resetGame = useCallback(() => {
    // Função de reset diário
    localStorage.removeItem('@PortalWordle:gameStatus');
    localStorage.removeItem('@PortalWordle:time');
    localStorage.removeItem('@PortalWordle:definition');
    localStorage.removeItem('@PortalWordle:accentedSolution');
    localStorage.removeItem('@PortalWordle:solution');
    localStorage.removeItem('@PortalWordle:repeats');
    setScore(0);
    setTime(0);
  }, []);

  // Função para verificar se a palavra do dia tem letras repetidas
  const checkForRepeatedLetters = useCallback((str: string) => {
    for (var i = 0; i < str.length; i++) {
      if (str.indexOf(str[i]) !== str.lastIndexOf(str[i])) {
        return true; //repete
      }
    }
    return false; // não repete
  }, []);

  const defineKeyWord = useCallback(async () => {
    // Função para definir a palavra do dia

    try {
      const response = await getWord();

      if (
        response.encryptedDefinition === '' ||
        response.encryptedNormalizedWord === '' ||
        response.encryptedWord === ''
      ) {
        setOpenModalError(true);
        resetGame();
        setLoading(false);
      } else if (
        localStorage.getItem('@PortalWordle:solution') !==
          response.encryptedNormalizedWord ||
        localStorage.getItem('@PortalWordle:accentedSolution') !==
          response.encryptedWord ||
        localStorage.getItem('@PortalWordle:definition') !==
          response.encryptedDefinition
      ) {
        resetGame();

        localStorage.setItem(
          '@PortalWordle:solution',
          response.encryptedNormalizedWord
        );
        localStorage.setItem(
          '@PortalWordle:accentedSolution',
          response.encryptedWord
        );
        localStorage.setItem(
          '@PortalWordle:definition',
          response.encryptedDefinition
        );

        var decrypted = CryptoJS.AES.decrypt(
          response.encryptedNormalizedWord,
          '5c81556179d6bfb07baf4badff8d9b3eb51b913f'
        );
        var decryptedAndAccented = CryptoJS.AES.decrypt(
          response.encryptedWord,
          '5c81556179d6bfb07baf4badff8d9b3eb51b913f'
        );
        var decryptedDefinition = CryptoJS.AES.decrypt(
          response.encryptedDefinition,
          '5c81556179d6bfb07baf4badff8d9b3eb51b913f'
        );

        const theresRepeatedCharacters = checkForRepeatedLetters(
          decrypted.toString(CryptoJS.enc.Utf8)
        );
        if (theresRepeatedCharacters) {
          localStorage.removeItem('@Portal:Wordle');
          let newBoardData: IBoard = {
            ...boardData,
            score: 0,
            rowIndex: 0,
            boardWords: [],
            boardRowStatus: [],
            presentCharArray: [],
            absentCharArray: [],
            correctCharArray: [],
            status: 'IN_PROGRESS',
            repeat: true,
          };
          setBoardData(newBoardData);
          localStorage.setItem('@Portal:Wordle', JSON.stringify(newBoardData));
        } else {
          localStorage.removeItem('@Portal:Wordle');
          let newBoardData: IBoard = {
            ...boardData,
            score: 0,
            rowIndex: 0,
            boardWords: [],
            boardRowStatus: [],
            presentCharArray: [],
            absentCharArray: [],
            correctCharArray: [],
            status: 'IN_PROGRESS',
            repeat: false,
          };
          setBoardData(newBoardData);
          localStorage.setItem('@Portal:Wordle', JSON.stringify(newBoardData));
        }

        setKeyWord(decrypted.toString(CryptoJS.enc.Utf8));
        setFinalAnswer(decryptedAndAccented.toString(CryptoJS.enc.Utf8));
        setDefinition(decryptedDefinition.toString(CryptoJS.enc.Utf8));
        setLoading(false);
        setIsOn(true);
      } else {
        setLoading(false);
      }
    } catch (err) {
      setOpenModalError(true);
    }
  }, [resetGame, boardData, checkForRepeatedLetters]);

  useEffect(() => {
    // useEffect inicial

    defineKeyWord();

    if (boardData && localStorage.getItem('@PortalWordle:gameStatus')) {
      setScore(boardData?.score);
    }

    if (!boardData || !boardData.status) {
      let newBoardData: IBoard = {
        ...boardData,
        score: 0,
        rowIndex: 0,
        boardWords: [],
        boardRowStatus: [],
        presentCharArray: [],
        absentCharArray: [],
        correctCharArray: [],
        status: 'IN_PROGRESS',
        repeat: false,
      };
      setBoardData(newBoardData);
      localStorage.setItem('@Portal:Wordle', JSON.stringify(newBoardData));
    }
  }, [boardData, defineKeyWord]);

  // useEffect de controle da modal com as regras do jogo e para iniciar a tela de carregamento
  useEffect(() => {
    setLoading(true);

    if (!localStorage.getItem('@PortalWordle:explanation')) {
      localStorage.setItem('@PortalWordle:explanation', 'true');
      setOpenModalExplanatory(true);
    }

    if (localStorage.getItem('@PortalWordle:gameStatus')) {
      setTime(Number(localStorage.getItem('@PortalWordle:time')));
      setIsOn(false);
      setOpenModalResult(true);
    }
  }, [setOpenModalExplanatory, setOpenModalResult]);

  const handleError = () => {
    // Função de controle e animação para erros
    setError(true);
    closeModalWarning(3, 'warning');
    setOpenModalInvalidWord(true);
  };

  // Função para fechar a modal de espera pós x segundos
  function closeModalWarning(tempo: number, method: string) {
    const timer = setInterval(() => {
      tempo--;
      if (tempo === 0) {
        if (method === 'warning') {
          setOpenModalWarning(false);
          setOpenModalInvalidWord(false);
        } else {
          setOpenModalWaiting(false);
        }
        stopInterval();
      }
    }, 1000);
    function stopInterval() {
      clearInterval(timer);
    }
  }

  const defineRepeatedCharacters = useCallback((str: string) => {
    const chars: any = [];
    for (const char of str) {
      chars[char] = (chars[char] || 0) + 1;
    }
    return Object.entries(chars)
      .filter((char: any) => char[1] > 1)
      .map((char) => char[0]);
  }, []);

  const enterBoardWord = (word: string) => {
    // Função acionada após clique do "ENTER"
    if (boardData) {
      let boardWords = boardData.boardWords;
      let boardRowStatus = boardData.boardRowStatus;
      let solution = keyWord;
      let presentCharArray = boardData.presentCharArray;
      let absentCharArray = boardData.absentCharArray;
      let correctCharArray = boardData.correctCharArray;
      let rowIndex = boardData.rowIndex;
      let rowStatus = [];
      let matchCount = 0;
      let status = boardData.status;
      let repeatedCharacters: any[] = [];

      defineAnimation(rowIndex);

      if (boardData.repeat)
        repeatedCharacters = defineRepeatedCharacters(solution);

      for (var index = 0; index < word.length; index++) {
        if (solution.charAt(index) === word.charAt(index)) {
          matchCount++;
          rowStatus.push('correct');
          if (!correctCharArray.includes(word.charAt(index)))
            correctCharArray.push(word.charAt(index));
          if (
            boardRowStatus[rowIndex] &&
            boardRowStatus[rowIndex].indexOf('present') !== -1 &&
            presentCharArray.indexOf(word.charAt(index)) !== -1
          ) {
            if (
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex].indexOf('present')
              ] === word.charAt(index)
            ) {
              if (boardData.repeat === false) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present'),
                  1,
                  'absent'
                );
              } else if (
                repeatedCharacters.indexOf(word.charAt(index)) === -1
              ) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present'),
                  1,
                  'absent'
                );
              }
            } else if (
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex][
                  boardRowStatus[rowIndex].indexOf('present') + 1
                ] === 'present'
                  ? boardRowStatus[rowIndex].indexOf('present') + 1
                  : boardRowStatus[rowIndex].indexOf('present')
              ] === word.charAt(index)
            ) {
              if (boardData.repeat === false) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present') + 1,
                  1,
                  'absent'
                );
              } else if (
                repeatedCharacters.indexOf(word.charAt(index)) === -1
              ) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present') + 1,
                  1,
                  'absent'
                );
              }
            } else if (
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex][
                  boardRowStatus[rowIndex].indexOf('present') + 2
                ] === 'present'
                  ? boardRowStatus[rowIndex].indexOf('present') + 2
                  : boardRowStatus[rowIndex].indexOf('present')
              ] === word.charAt(index)
            ) {
              if (boardData.repeat === false) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present') + 2,
                  1,
                  'absent'
                );
              } else if (
                repeatedCharacters.indexOf(word.charAt(index)) === -1
              ) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present') + 2,
                  1,
                  'absent'
                );
              }
            } else if (
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex][
                  boardRowStatus[rowIndex].indexOf('present') + 3
                ] === 'present'
                  ? boardRowStatus[rowIndex].indexOf('present') + 3
                  : boardRowStatus[rowIndex].indexOf('present')
              ] === word.charAt(index)
            ) {
              if (boardData.repeat === false) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present') + 3,
                  1,
                  'absent'
                );
              } else if (
                repeatedCharacters.indexOf(word.charAt(index)) === -1
              ) {
                boardRowStatus[rowIndex].splice(
                  boardRowStatus[rowIndex].indexOf('present') + 3,
                  1,
                  'absent'
                );
              }
            }
          }
          if (presentCharArray.indexOf(word.charAt(index)) !== -1)
            presentCharArray.splice(
              presentCharArray.indexOf(word.charAt(index)),
              1
            );
        } else if (solution.includes(word.charAt(index))) {
          rowStatus.push('present');
          if (
            boardRowStatus[rowIndex] &&
            boardRowStatus[rowIndex].indexOf('correct') !== -1 &&
            correctCharArray.indexOf(word.charAt(index)) !== -1
          ) {
            if (
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex].indexOf('correct')
              ] === word.charAt(index) ||
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex][
                  boardRowStatus[rowIndex].indexOf('correct') + 1
                ] === 'correct'
                  ? boardRowStatus[rowIndex].indexOf('correct') + 1
                  : boardRowStatus[rowIndex].indexOf('correct')
              ] === word.charAt(index) ||
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex][
                  boardRowStatus[rowIndex].indexOf('correct') + 2
                ] === 'correct'
                  ? boardRowStatus[rowIndex].indexOf('correct') + 2
                  : boardRowStatus[rowIndex].indexOf('correct')
              ] === word.charAt(index) ||
              boardData.boardWords[rowIndex][
                boardRowStatus[rowIndex][
                  boardRowStatus[rowIndex].indexOf('correct') + 3
                ] === 'correct'
                  ? boardRowStatus[rowIndex].indexOf('correct') + 3
                  : boardRowStatus[rowIndex].indexOf('correct')
              ] === word.charAt(index)
            ) {
              if (boardData.repeat === false) {
                boardRowStatus[rowIndex].splice(index, 1, 'absent');
              } else if (
                repeatedCharacters.indexOf(word.charAt(index)) === -1
              ) {
                boardRowStatus[rowIndex].splice(index, 1, 'absent');
              }
            }
          }
          if (!presentCharArray.includes(word.charAt(index)))
            presentCharArray.push(word.charAt(index));
        } else {
          rowStatus.push('absent');
          if (!absentCharArray.includes(word.charAt(index)))
            absentCharArray.push(word.charAt(index));
        }

        if (matchCount === 5) {
          defineScore(rowIndex);
          status = 'WIN';
          setWinCondition('WIN');
          setIsOn(false);
          setTimeout(() => {
            localStorage.setItem('@PortalWordle:gameStatus', 'WIN');
            setOpenModalResult(true);
          }, 2700);
          localStorage.setItem('@PortalWordle:time', time.toString());
        } else if (rowIndex + 1 === 6 && boardData) {
          status = 'LOSE';
          setWinCondition('LOSE');
          setIsOn(false);
          setTimeout(() => {
            localStorage.setItem('@PortalWordle:gameStatus', 'LOSE');
            setOpenModalResult(true);
          }, 2700);
          localStorage.setItem('@PortalWordle:time', time.toString());
        }
        boardRowStatus[rowIndex] = rowStatus;
        boardWords[rowIndex] = word;
        let newBoardData = {
          ...boardData,
          boardWords: boardWords,
          boardRowStatus: boardRowStatus,
          rowIndex: rowIndex + 1,
          status: status,
          presentCharArray: presentCharArray,
          absentCharArray: absentCharArray,
          correctCharArray: correctCharArray,
        };
        setBoardData(newBoardData);
        localStorage.setItem('@Portal:Wordle', JSON.stringify(newBoardData));
      }
    }
  };

  const defineAnimation = (index: number) => {
    // Função para definir a animação da linha correta
    switch (index) {
      case 0:
        setAnimation0(true);
        break;
      case 1:
        setAnimation1(true);
        break;
      case 2:
        setAnimation2(true);
        break;
      case 3:
        setAnimation3(true);
        break;
      case 4:
        setAnimation4(true);
        break;
      case 5:
        setAnimation5(true);
        break;
    }
  };

  const enterCurrentText = (word: string) => {
    // Função para escrita da palavra
    if (boardData) {
      let boardWords = boardData.boardWords;
      let rowIndex = boardData.rowIndex;
      boardWords[rowIndex] = word;
      let newBoardData = {
        ...boardData,
        boardWords: boardWords,
      };
      setBoardData(newBoardData);
    }
  };

  const handleKeyPress = async (key: never) => {
    // Função para controle de cliques
    if (boardData) {
      if (boardData.rowIndex > 5 || boardData.status === 'WIN') return;
      if (key === 'ENTER') {
        setError(false);
        if (charArray.length === 5) {
          setOpenModalWaiting(true);
          closeModalWarning(2, 'waiting');
          let word = charArray.join('').toLowerCase();
          if (await verifyWord(word)) {
            enterBoardWord(word);
            setCharArray([]);
          } else {
            handleError();
            return;
          }
        } else {
          setError(true);
          setOpenModalWarning(true);
          closeModalWarning(3, 'warning');
        }
        return;
      }
      if (key === '⌫') {
        charArray.splice(charArray.length - 1, 1);
        setCharArray([...charArray]);
      } else if (charArray.length < 5) {
        charArray.push(key);
        setCharArray([...charArray]);
      }
      enterCurrentText(charArray.join('').toLowerCase());
    }
  };

  function defineScore(row: number) {
    // Função para definir a pontuação final
    if (boardData) {
      switch (row) {
        case 0:
          setScore(100);
          boardData.score = 100;
          break;
        case 1:
          setScore(70);
          boardData.score = 70;
          break;
        case 2:
          setScore(50);
          boardData.score = 50;
          break;
        case 3:
          setScore(40);
          boardData.score = 40;
          break;
        case 4:
          setScore(25);
          boardData.score = 25;
          break;
        case 5:
          setScore(10);
          boardData.score = 10;
          break;
      }
    }
  }

  return (
    <Container>
      <GameHeader
        maxScore={100}
        type="WORDLE"
        currentScore={score}
        menuItems={[
          {
            buttonFunctions: () => {
              setOpenModalExplanatory(true);
            },
            buttonText: 'Regras',
          },
          {
            buttonFunctions: () => {
              setOpenModalBorderColor(true);
            },
            buttonText: 'Alterar coloração do jogo',
          },
        ]}
      />
      <Content>
        <GameContent>
          <div>
            <ModalWarning
              isVisible={openModalWarning ? true : false}
              zIndex={6}
            >
              <h1>Complete a palavra antes de continuar!</h1>
            </ModalWarning>

            <ModalWarning
              isVisible={openModalInvalidWord ? true : false}
              zIndex={6}
            >
              <h1>Digite uma palavra existente para continuar!</h1>
            </ModalWarning>

            <ModalWarning
              isVisible={openModalWaiting ? true : false}
              zIndex={5}
            >
              <h1>Aguarde um instante!</h1>
            </ModalWarning>

            <Rules isVisible={openModalExplanatory} />

            <Results
              isVisible={
                boardData?.status !== 'IN_PROGRESS' ? openModalResult : false
              }
              answer={finalAnswer}
              definition={definition}
              result={winCondition}
            />

            <ModalError isVisible={openModalError ? true : false}>
              <main>
                <span>ERRO</span>
                <h2>Aconteceu algum erro!</h2>
                <div>
                  <p>Recarregue a página e tente novamente!</p>
                  <img
                    src={OwlError}
                    alt="Desenho de uma coruja brava pelo erro ocorrido"
                  />
                </div>
              </main>
            </ModalError>

            <BorderOptions isVisible={openModalBorderColor} />

            <GameContainer>
              <main>
                {loading ? (
                  <LoadingPage keyWordExists={loading ? false : true}>
                    <img
                      src={Logo}
                      alt="Imagem de introdução ao jogo, apresentando a logotipo da empresa
                        A Recreativa, mostrando uma imagem de três corujas (pai, mãe e filho) do lado 
                        esquerdo e o nome da empresa do lado direito"
                    />
                    <h1>Aguarde um instante...</h1>
                  </LoadingPage>
                ) : (
                  [0, 1, 2, 3, 4, 5].map((row, rowIndex: number) => (
                    <div
                      className={`cube-row ${
                        boardData &&
                        row === boardData.rowIndex &&
                        error &&
                        'error'
                      }`}
                      key={rowIndex}
                    >
                      <GameBoard
                        row={row}
                        index={rowIndex}
                        boardData={boardData}
                      />
                    </div>
                  ))
                )}
              </main>
            </GameContainer>
            <Sidebar
              maxScore={100}
              currentScore={score}
              menuItems={[
                {
                  buttonFunctions: () => {
                    setOpenModalExplanatory(true);
                  },
                  buttonText: 'Regras',
                },
                {
                  buttonFunctions: () => {
                    setOpenModalBorderColor(true);
                  },
                  buttonText: 'Alterar coloração do jogo',
                },
              ]}
            >
              <Keyboard boardData={boardData} handleKeyPress={handleKeyPress} />
            </Sidebar>
          </div>
          <div className="mobileKeyboard">
            <Keyboard boardData={boardData} handleKeyPress={handleKeyPress} />
          </div>
        </GameContent>
        <p style={{ background: '#d6d6d6' }}>
          VER MAIS COMENTÁRIOS <IoTriangleSharp size={12} />
        </p>
      </Content>
    </Container>
  );
};

export default Wordle;
